import { captureMeta, greetingFor } from './format.js';

export const el = (id) => document.getElementById(id);

/** A fresh copy of a `<template>`'s single root element, ready to fill in and append. */
export const clone = (id) => document.getElementById(id).content.firstElementChild.cloneNode(true);

export function showLogin() {
  el('login-view').hidden = false;
  el('app-view').hidden = true;
}

export function showApp() {
  el('login-view').hidden = true;
  el('app-view').hidden = false;
  el('greeting').textContent = greetingFor(new Date());
  showHome();
}

const VIEWS = ['home', 'note', 'calendar', 'class', 'study', 'notepage',
               'timer', 'quiz'];

/**
 * Exactly one content view is ever visible. `navKey` is what the sidebar
 * highlights, which is not always the view name: studying a set keeps its
 * class lit, because that is where Back goes.
 */
export function showView(name, navKey = name) {
  for (const view of VIEWS) el(`${view}-view`).hidden = view !== name;
  for (const row of document.querySelectorAll('#side-nav .nav-row')) {
    row.classList.toggle('is-on', row.dataset.nav === navKey);
  }
}

export function showHome() {
  showView('home');
}

export function showNote() {
  showView('note');
  el('note-text').focus();
}

export function showLoginError(message) {
  const node = el('login-error');
  node.textContent = message;
  node.hidden = !message;
}

export function setStatus(message, kind) {
  const node = el('upload-status');
  node.textContent = message;
  node.className = kind ? `status is-${kind}` : 'status';
  node.hidden = !message;
}

export function setProgress(percent) {
  el('upload-progress').hidden = percent === null;
  if (percent !== null) el('upload-progress-bar').style.width = `${percent}%`;
}

/**
 * One row of the upload history.
 *
 * Built with createElement and textContent rather than innerHTML: a filename
 * is untrusted input and must never be parsed as markup.
 */
function captureRow(memo) {
  const li = document.createElement('li');
  li.className = 'capture';
  if (memo?.key) li.dataset.key = memo.key;

  const kind = document.createElement('div');
  kind.className = 'capture-kind';
  kind.textContent = 'REC';

  const body = document.createElement('div');
  const title = document.createElement('div');
  title.className = 'capture-title';
  title.textContent = memo?.name || 'recording';
  const meta = document.createElement('div');
  meta.className = 'capture-meta';
  meta.textContent = captureMeta(memo);
  body.append(title, meta);

  li.append(kind, body);
  return li;
}

/**
 * The whole history, newest first, as the API gave it.
 *
 * Safe to call again: it clears its own rows first, which is what makes a
 * refresh after a slow reply work rather than duplicating everything -- the
 * same rule `screens.mountSidebar` follows.
 *
 * Not re-sorted here. One place decides what a history reads like, and it is
 * the API.
 */
export function showCaptures(memos) {
  const list = el('captures-list');
  list.replaceChildren(...(memos || []).map(captureRow));
  el('captures-empty').hidden = (memos || []).length > 0;
}

/**
 * One upload, on screen the moment it lands.
 *
 * Prepended rather than waiting for a re-fetch: the recording somebody has
 * just sent up is the one they are looking at the list to see, and a round
 * trip before it appears reads as the upload not having worked.
 */
export function addCapture(memo) {
  el('captures-empty').hidden = true;
  el('captures-list').prepend(captureRow(memo));
}

/** Said in the panel itself, so a history that cannot be reached does not
 *  read as a history that is empty -- which is the same recording either way
 *  to look at, and two completely different things to do about. */
export function captureTrouble(message) {
  const empty = el('captures-empty');
  empty.textContent = message || 'Nothing uploaded yet.';
  empty.hidden = false;
}
